import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { applyCertificate } from "../../store/slices/certificates/asyncThunks";
import useScrollLock from "../../hooks/useScrollLock";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  CloseButton,
  ModalTitle,
  ModalContent,
  FormGroup,
  Label,
  TextArea,
  SubmitButton,
  CancelButton,
  ButtonGroup,
  Instructions,
  InstructionTitle,
  InstructionList,
  InstructionItem,
  HiddenFileInput,
  CustomUploadButton,
  ImagePreviewGrid,
  ImagePreviewItem,
  PreviewImage,
  DeleteImageButton,
  ImageName,
  SelectedImagesInfo,
} from "./CertificateApplicationModal.styles";
import { FaTimes, FaCertificate, FaUpload, FaTrash } from "react-icons/fa";

const CertificateApplicationModal = ({
  isOpen,
  onClose,
  courseId,
  courseTitle,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    description: "",
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const imageInputRef = useRef(null);
  const dispatch = useDispatch();

  useScrollLock(isOpen);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    // Validate total count including existing images
    const totalImages = previewImages.length + files.length;
    if (totalImages > 5) {
      toast.error(
        `Možete okačiti maksimalno 5 slika ukupno. Trenutno imate ${previewImages.length} slika.`,
      );
      return;
    }

    // Validate files first
    const validFiles = [];
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`Fajl ${file.name} nije slika`);
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Slika ${file.name} je prevelika (maksimalno 10MB)`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Add valid files to selectedImages immediately
    setSelectedImages((prev) => [...prev, ...validFiles]);

    // Create previews asynchronously
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImage = {
          id: Date.now() + Math.random(),
          file,
          url: event.target.result,
          name: file.name,
        };

        setPreviewImages((prev) => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });

    // Clear the input
    e.target.value = "";
  };

  const removePreviewImage = (imageId) => {
    const imageToRemove = previewImages.find((img) => img.id === imageId);
    if (imageToRemove) {
      setPreviewImages((prev) => prev.filter((img) => img.id !== imageId));
      setSelectedImages((prev) =>
        prev.filter((file) => file !== imageToRemove.file),
      );
    }
  };

  const triggerImageUpload = () => {
    imageInputRef.current?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedImages.length === 0) {
      toast.error("Molimo odaberite slike vašeg rada");
      return;
    }

    if (selectedImages.length < 2) {
      toast.error("Molimo odaberite najmanje 2 slike vašeg rada");
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      formDataToSend.append("description", formData.description);

      selectedImages.forEach((image) => {
        formDataToSend.append("workImages", image);
      });

      const result = await dispatch(applyCertificate({
        courseId,
        data: formDataToSend
      }));

      if (result.type === 'certificates/applyCertificate/fulfilled') {
        toast.success("Uspešno ste aplicirali za sertifikat!");
        onSuccess && onSuccess();
        resetForm();
        onClose();
      } else {
        throw new Error(
          result.error?.message || "Greška prilikom apliciranja za sertifikat",
        );
      }
    } catch (error) {
      console.error("Application error:", error);
      toast.error(
        error.message || "Greška prilikom apliciranja za sertifikat",
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ description: "" });
    setSelectedImages([]);
    setPreviewImages([]);
  };

  const isFormDirty =
    formData.description.trim() !== "" ||
    selectedImages.length > 0 ||
    previewImages.length > 0;

  const handleClose = () => {
    if (
      isFormDirty &&
      !window.confirm(
        "Imate nesačuvane podatke u formi. Da li sigurno želite da zatvorite?",
      )
    ) {
      return;
    }
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FaCertificate />
            Apliciranje za Sertifikat
          </ModalTitle>
          <CloseButton onClick={handleClose}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <Instructions>
            <InstructionTitle>
              <FaUpload />
              Instrukcije za aplikaciju
            </InstructionTitle>
            <p>
              Da biste dobili sertifikat za kurs{" "}
              <strong>"{courseTitle}"</strong>, potrebno je da:
            </p>
            <InstructionList>
              <InstructionItem>
                Fotografišite svoj rad gde prikazujete naučene tehnike iz kursa
              </InstructionItem>
              <InstructionItem>
                Odaberite 2-5 najkvalitetnijih slika vašeg rada
              </InstructionItem>
              <InstructionItem>
                Okačite slike koristeći formu ispod (maksimalno 10MB po slici)
              </InstructionItem>
              <InstructionItem>
                Dodajte kratki opis vašeg rada (opciono)
              </InstructionItem>
            </InstructionList>
            <p>
              <strong>Napomena:</strong> Administrator će pregledati vaše slike
              i poslati vam sertifikat.
            </p>
          </Instructions>

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>
                <FaUpload />
                Slike vašeg rada (2-5 slika) *
              </Label>
              <HiddenFileInput
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
              />
              <CustomUploadButton type="button" onClick={triggerImageUpload}>
                <FaUpload />
                Odaberite slike
              </CustomUploadButton>

              {previewImages.length > 0 && (
                <>
                  <SelectedImagesInfo>
                    <p>
                      <strong>Odabrane slike:</strong> {previewImages.length}/5
                    </p>
                  </SelectedImagesInfo>
                  <ImagePreviewGrid>
                    {previewImages.map((image) => (
                      <ImagePreviewItem key={image.id}>
                        <PreviewImage src={image.url} alt={image.name} />
                        <DeleteImageButton
                          type="button"
                          onClick={() => removePreviewImage(image.id)}
                          title="Ukloni sliku"
                        >
                          <FaTrash />
                        </DeleteImageButton>
                        <ImageName>{image.name}</ImageName>
                      </ImagePreviewItem>
                    ))}
                  </ImagePreviewGrid>
                </>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Opis vašeg rada (opciono)</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Ukratko opišite tehnike koje ste koristili i šta ste naučili iz kursa..."
                rows={4}
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton type="button" onClick={handleClose}>
                Otkaži
              </CancelButton>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? "Aplikujem..." : "Apliciraj za Sertifikat"}
              </SubmitButton>
            </ButtonGroup>
          </form>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CertificateApplicationModal;
